import React, { useState, useEffect, useRef, useCallback } from "react";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import 'chart.js/auto';
import axios from "axios";
import {
  Container, Row, Col, Card, Alert, Button, Form, Table, Badge, ProgressBar,
  Modal, Spinner
} from "react-bootstrap";
import {
  FaArrowUp, FaArrowDown, FaInfoCircle, FaClock, FaExclamationTriangle,
  FaSync, FaCheckCircle, FaPlayCircle, FaDownload, FaSearch, FaUser,
  FaPlay, FaList, FaTrophy, FaPause, FaChartLine
} from "react-icons/fa";

const formatDuration = (hours) => {
  const h = Math.floor(hours);
  const m = Math.floor((hours - h) * 60);
  const s = Math.round(((hours - h) * 60 - m) * 60);

  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
};

const PlayerAnalytics = ({ partnerId }) => {
  // State Management
  const [activeTab, setActiveTab] = useState("overview");
  const getDefaultDateRange = () => {
    const s = new Date();
    s.setDate(s.getDate() - 7);
    s.setHours(0, 0, 0, 0);
    return {
      startDate: s.toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [quickFilter, setQuickFilter] = useState("last7days");

  // Pending (unapplied) filter values bound to the inputs
  const [pendingRange, setPendingRange] = useState({ ...dateRange });
  const [pendingQuickFilter, setPendingQuickFilter] = useState(quickFilter);

  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [errorMsg, setErrorMsg] = useState(null);

  // Analytics Data State
  const [overviewData, setOverviewData] = useState({
    totalSessions: 0,
    totalWatchTime: "0h 0m 0s",
    completionRate: 0,
    errorRate: 0,
    trendsData: {
      labels: [],
      sessions: [],
      watchTime: []
    }
  });

  const [deviceData, setDeviceData] = useState({
    desktop: 0,
    mobile: 0,
    tablet: 0,
    smartTv: 0
  });

  const [performanceData, setPerformanceData] = useState({
    bufferingEvents: 0,
    qualityChanges: 0,
    errorRate: 0,
    avgLoadTime: 0
  });

  const [engagementData, setEngagementData] = useState({
    avgSessionDuration: 0,
    seekEvents: 0,
    skipEvents: 0,
    heatmapData: []
  });

  // Daily performance incidents time series
  const [performanceSeries, setPerformanceSeries] = useState([]);

  const [realTimeMetrics, setRealTimeMetrics] = useState({
    activeSessions: 0,
    currentViewers: 0,
    peakConcurrentUsers: 0
  });

  // Popular tab state
  const [popularLoading, setPopularLoading] = useState(false);
  const [popularSortBy, setPopularSortBy] = useState('watchHours');
  const [popularVideos, setPopularVideos] = useState([]);
  const [popularPage, setPopularPage] = useState(1);
  const [popularPageSize, setPopularPageSize] = useState(10);

  // Single video analytics state
  const [videoSearchQuery, setVideoSearchQuery] = useState("");
  const [videoSearchResults, setVideoSearchResults] = useState([]);
  const [showVideoDropdown, setShowVideoDropdown] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [singleVideoAnalytics, setSingleVideoAnalytics] = useState(null);

  // Last 7 days summary for Popular tab (legacy)
  const [last7DaysData, setLast7DaysData] = useState(null);
  // Week-over-Week summary for Popular tab (new)
  const [weekComparison, setWeekComparison] = useState(null);

  // Summary and Table State (new endpoints)
  const [summary, setSummary] = useState({
    totalUsers: 0,
    totalSessions: 0,
    totalWatchHours: 0,
    avgWatchHoursPerUser: 0,
    avgWatchHoursPerSession: 0,
    playCount: 0,
    pauseCount: 0,
    errorCount: 0,
    topErrors: []
  });

  const [tableData, setTableData] = useState({
    items: [],
    page: 1,
    pageSize: 10,
    total: 0
  });
  const [tableFilters, setTableFilters] = useState({ userId: "", contentId: "" });
  const [tableSort, setTableSort] = useState({ sortBy: "created_at", sortDir: "DESC" });
  const [tableLoading, setTableLoading] = useState(false);

  // Export & comparison states (Sessions tab)
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareRange, setCompareRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [exportAllPages, setExportAllPages] = useState(false);

  // Debounce timer for Sessions tab filters
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Video Details Modal State
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalVideoData, setModalVideoData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(5);

  // API Configuration
  const analyticsEndpoint = `${process.env.REACT_APP_Baseurl}/analytics/player`;
  const apiKey = process.env.REACT_APP_ANALYTICS_API_KEY || 'fa-demo-2025-12345678';
  const tenantId = localStorage.getItem('tenant_id');
  const role = localStorage.getItem('role');
  const userId = localStorage.getItem('user_id');

  // Determine which partner context to use
  // Priority: (1) explicit prop from Admin selector, (2) current logged-in partner, (3) none (all data)
  const partnerIdFromStorage = localStorage.getItem('partner_id');
  const effectivePartnerId = partnerId || (role === "4" ? partnerIdFromStorage : undefined);

  // Fetch Analytics Data
  const fetchAnalyticsData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setErrorMsg(null);

      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };

      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        refresh: forceRefresh,
        partner_id: effectivePartnerId
      };

      // Fetch Overview Data
      const overviewResponse = await axios.get(`${analyticsEndpoint}/overview`, { headers, params });
      // Fetch Device Distribution
      const deviceResponse = await axios.get(`${analyticsEndpoint}/devices`, { headers, params });
      // Fetch Performance Metrics
      const performanceResponse = await axios.get(`${analyticsEndpoint}/performance`, { headers, params });
      // Fetch Engagement Data
      const engagementResponse = await axios.get(`${analyticsEndpoint}/engagement`, { headers, params });
      // Fetch Real-time Metrics
      const realtimeResponse = await axios.get(`${analyticsEndpoint}/realtime`, { headers });
      // Fetch Performance Timeseries (errors, buffering, quality changes)
      const perfSeriesResponse = await axios.get(`${analyticsEndpoint}/performance/timeseries`, { headers, params });

      // Update state strictly from API (no static fallbacks)
      if (overviewResponse?.data) setOverviewData(overviewResponse.data);
      if (deviceResponse?.data) setDeviceData(deviceResponse.data);
      if (performanceResponse?.data) setPerformanceData(performanceResponse.data);
      if (engagementResponse?.data) setEngagementData(engagementResponse.data);
      if (realtimeResponse?.data) setRealTimeMetrics(realtimeResponse.data);
      if (perfSeriesResponse?.data) setPerformanceSeries(perfSeriesResponse.data?.data || perfSeriesResponse.data || []);

      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      // Fallback: stay silent on network errors or keep previous message if already there
      const isNetworkError = !error.response && error.request;
      if (!isNetworkError) {
        setErrorMsg(error?.response?.data?.message || error.message || 'Failed to fetch analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed analytics for a specific video (Modal)
  const handleShowDetails = async (video) => {
    setShowDetailsModal(true);
    setModalLoading(true);
    setModalVideoData(null);
    setModalPage(1);
    try {
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
      };
      const params = {
        // We omit startDate/endDate to fetch ALL results (lifetime) for this video
        // as per user request: "should not today result i wnat all the result"
      };
      const vid = video.id || video.content_id || video.video_id;
      const resp = await axios.get(`${analyticsEndpoint}/video/${vid}/watch-hours`, { headers, params });
      if (resp.data && resp.data.success) {
        setModalVideoData(resp.data.data);
      } else {
        setModalVideoData(resp.data); // Fallback if not wrapped
      }
    } catch (err) {
      console.error("Failed to fetch video details:", err);
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch summary data (new)
  const fetchSummaryData = async () => {
    try {
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        partner_id: partnerId || undefined
      };
      const res = await axios.get(`${analyticsEndpoint}/summary`, { headers, params });
      if (res?.data) setSummary(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching summary:', err);
      // Fallback: stay silent, keep existing summary
    }
  };

  // Fetch sessions table (new)
  const fetchTableData = async () => {
    try {
      setTableLoading(true);
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: tableData.page,
        pageSize: tableData.pageSize,
        sortBy: tableSort.sortBy,
        sortDir: tableSort.sortDir,
        userId: tableFilters.userId || undefined,
        contentId: tableFilters.contentId || undefined,
        partner_id: effectivePartnerId
      };
      const res = await axios.get(`${analyticsEndpoint}/table`, { headers, params });
      if (res?.data) {
        setTableData(prev => ({
          ...prev,
          items: res.data.items || [],
          total: res.data.total || 0,
          page: res.data.page || prev.page,
          pageSize: res.data.pageSize || prev.pageSize,
        }));
      }
    } catch (err) {
      console.error('Error fetching table:', err);
      // Fallback: stay silent, keep previous table state
    } finally {
      setTableLoading(false);
    }
  };

  // Generate sample heatmap data (kept for dev-only; not used in prod now)
  const generateSampleHeatmapData = () => {
    const data = [];
    for (let i = 0; i < 100; i++) {
      data.push({
        time: i,
        engagement: Math.random() * 100,
        interactions: Math.floor(Math.random() * 50)
      });
    }
    return data;
  };

  // Handle date range changes (pending only)
  const handlePendingDateChange = (field, value) => {
    setPendingRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters: commit pending -> active and trigger fetch via effects
  const applyFilters = () => {
    setQuickFilter(pendingQuickFilter);
    setDateRange({ ...pendingRange });
  };

  // Keep pending state in sync when active state changes programmatically
  useEffect(() => {
    fetchAnalyticsData();
    fetchSummaryData();
    fetchTableData();
  }, [dateRange.startDate, dateRange.endDate, partnerId]);

  useEffect(() => {
    setPendingQuickFilter(quickFilter);
  }, [quickFilter]);

  // Handle quick filter changes (pending only)
  const handlePendingQuickFilter = (filter) => {
    setPendingQuickFilter(filter);
    const now = new Date();
    let startDate;
    let endDate;
    switch (filter) {
      case "today": {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        startDate = t;
        endDate = new Date();
        break;
      }
      case "yesterday": {
        const y = new Date();
        y.setDate(y.getDate() - 1);
        y.setHours(0, 0, 0, 0);
        startDate = y;
        const ye = new Date(y);
        endDate = ye; // both start and end are yesterday
        break;
      }
      case "last7days": {
        const s = new Date();
        s.setDate(s.getDate() - 7);
        s.setHours(0, 0, 0, 0);
        startDate = s;
        endDate = new Date();
        break;
      }
      case "last30days": {
        const s = new Date();
        s.setDate(s.getDate() - 30);
        s.setHours(0, 0, 0, 0);
        startDate = s;
        endDate = new Date();
        break;
      }
      default:
        return;
    }
    setPendingRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  // Handle quick filter changes (commit immediately in code paths that require it)
  const handleQuickFilter = (filter) => {
    setQuickFilter(filter);
    const now = new Date();
    let startDate;
    let endDate;

    switch (filter) {
      case "today": {
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      }
      case "yesterday": {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        break;
      }
      case "last7days": {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      }
      case "last30days": {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      }
      default:
        return;
    }

    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  // Popular: fetch popular videos
  const fetchPopularVideos = async () => {
    try {
      setPopularLoading(true);
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy: popularSortBy,
        limit: 20,
        partner_id: effectivePartnerId
      };

      // Try /popular first; fallback to /top-content if not available
      let res;
      try {
        res = await axios.get(`${analyticsEndpoint}/popular`, { headers, params });
      } catch (e) {
        res = await axios.get(`${analyticsEndpoint}/top-content`, { headers, params });
      }
      const data = res?.data?.data || res?.data || {};
      const videos = data.videos || data || [];
      setPopularVideos(videos);
    } catch (err) {
      console.error('Error fetching popular videos:', err);
      // Fallback: stay silent, don't clear the list if we already have data
    } finally {
      setPopularLoading(false);
    }
  };

  // Popular: search videos
  const searchVideos = async (query) => {
    if (!query) { setVideoSearchResults([]); return; }
    try {
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const res = await axios.get(`${analyticsEndpoint}/videos/search`, { headers, params: { search: query, limit: 10 } });
      const videos = res?.data?.videos || res?.data?.data || [];
      setVideoSearchResults(videos);
      setShowVideoDropdown(true);
    } catch (err) {
      console.error('Error searching videos:', err);
      setVideoSearchResults([]);
    }
  };

  // Popular: fetch single video analytics
  const fetchSingleVideoAnalytics = async (videoId) => {
    if (!videoId) return;
    try {
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        partner_id: partnerId || undefined
      };
      // Prefer watch-hours endpoint as implemented in microservice
      const res = await axios.get(`${analyticsEndpoint}/video/${videoId}/watch-hours`, { headers, params });
      // Normalize response shape
      const d = res?.data?.data || res?.data || {};
      const metrics = d.metrics || d || {};
      setSingleVideoAnalytics({
        totalWatchHours: Number(metrics.totalWatchHours || metrics.total_watch_hours || 0).toFixed(2),
        uniqueViewers: metrics.uniqueViewers || metrics.unique_viewers || 0,
        avgWatchDuration: Number(metrics.avgWatchDuration || metrics.avg_watch_duration || 0).toFixed(1),
        avgCompletion: Number(metrics.avgCompletion || metrics.avg_completion || 0).toFixed(1),
        daily: d.dailyTrend || d.daily || [],
      });
    } catch (err) {
      console.error('Error fetching single video analytics:', err);
      // Fallback: stay silent, keep previous data if any
    }
  };

  // Popular: fetch week-over-week analytics (current vs past week)
  const fetchWeekComparison = async (weekStartOverride = null) => {
    try {
      const headers = {
        'X-API-Key': apiKey,
        ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        'Content-Type': 'application/json'
      };
      const computeMonday = (dateStr) => {
        const d = dateStr ? new Date(dateStr) : new Date();
        const day = d.getDay();
        const diff = (day === 0 ? -6 : 1) - day; // Monday as start
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d.toISOString().split('T')[0];
      };
      const weekStart = weekStartOverride || computeMonday(dateRange.endDate);
      const res = await axios.get(`${analyticsEndpoint}/week-comparison`, { headers, params: { weekStart } });
      const d = res?.data?.data || res?.data || {};
      setWeekComparison(d);
    } catch (err) {
      console.error('Error fetching week comparison analytics:', err);
      // Fallback: stay silent, keep existing week comparison data
    }
  };

  // Export player events (legacy)
  const handleExport = async () => {
    try {
      const response = await axios.get(`${analyticsEndpoint}/export`, {
        headers: { 'X-API-Key': apiKey },
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          format: 'csv'
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `player-analytics-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // ----- Sessions CSV export helpers -----
  const buildHeaders = () => ({
    'X-API-Key': apiKey,
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    'Content-Type': 'application/json'
  });

  const fetchTablePage = async (range, filters, sort, page, pageSize) => {
    const params = {
      startDate: range.startDate,
      endDate: range.endDate,
      page,
      pageSize,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
      userId: filters.userId || undefined,
      contentId: filters.contentId || undefined,
      partner_id: effectivePartnerId
    };
    const res = await axios.get(`${analyticsEndpoint}/table`, { headers: buildHeaders(), params });
    return res?.data || { items: [], total: 0, page, pageSize };
  };

  const fetchAllSessions = async (range, filters, sort, maxRows = 10000) => {
    // Pull all pages up to maxRows to avoid huge downloads
    let page = 1;
    const pageSize = 200; // reasonable batch size
    const out = [];
    let total = 0;
    do {
      const data = await fetchTablePage(range, filters, sort, page, pageSize);
      total = data.total || 0;
      if (Array.isArray(data.items)) out.push(...data.items);
      if (out.length >= Math.min(total, maxRows)) break;
      page += 1;
    } while ((page - 1) * 200 < Math.min(total, maxRows));
    return out.slice(0, maxRows);
  };

  const sanitize = (v) => {
    if (v == null) return '';
    const s = String(v);
    // Escape quotes and wrap with quotes if contains comma or newline
    const escaped = s.replace(/"/g, '""');
    if (/[",\n]/.test(escaped)) return `"${escaped}"`;
    return escaped;
  };

  const mapRowForCSV = (item, periodLabel = '') => ({
    Period: periodLabel,
    CreatedAt: item.created_at || '',
    SessionID: item.session_id || '',
    StartedAt: item.started_at || '',
    EndedAt: item.ended_at || '',
    UserID: item.display_user_id || item.user_id || '',
    Role: item.role || (item.user_id ? 'user' : 'guest'),
    Country: item.country || '',
    VideoID: item.video_id || item.video?.id || '',
    Title: item.video_title || '',
    Source: item.video_source || item.video?.type || '',
    WatchHours: Number(item.watch_hours || 0).toFixed(2),
    CompletionRate: Number(item.completion_rate || item.completion_percentage || 0).toFixed(2),
    Plays: item.play_count || 0,
    Pauses: item.pause_count || 0,
    Errors: item.error_count || 0,
    Device: item.device_summary || item.device?.deviceType || item.device?.type,
  });

  const rowsToCSV = (rows) => {
    if (!rows || rows.length === 0) return '';
    const headers = Object.keys(rows[0]);
    const headerLine = headers.map(sanitize).join(',');
    const dataLines = rows.map(r => headers.map(h => sanitize(r[h])).join(','));
    return [headerLine, ...dataLines].join('\n');
  };

  const downloadTextFile = (filename, text, mime = 'text/csv') => {
    const blob = new Blob([text], { type: mime });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const computeSummary = (items) => {
    const totalSessions = items.length;
    const sumWatchHours = items.reduce((s, x) => s + Number(x.watch_hours || 0), 0);
    const avgCompletion = items.length ? (items.reduce((s, x) => s + Number(x.completion_rate || x.completion_percentage || 0), 0) / items.length) : 0;
    const plays = items.reduce((s, x) => s + Number(x.play_count || 0), 0);
    const pauses = items.reduce((s, x) => s + Number(x.pause_count || 0), 0);
    const errors = items.reduce((s, x) => s + Number(x.error_count || 0), 0);
    return { totalSessions, sumWatchHours, avgCompletion, plays, pauses, errors };
  };

  const buildComparisonSection = (labelA, sumA, labelB, sumB) => {
    const pct = (a, b) => (b > 0 ? (((a - b) / b) * 100).toFixed(2) : '0.00');
    const lines = [
      ['Metric', labelA, labelB, 'Delta', 'Delta %'],
      ['Total Sessions', sumA.totalSessions, sumB.totalSessions, sumA.totalSessions - sumB.totalSessions, pct(sumA.totalSessions, sumB.totalSessions)],
      ['Watch Hours', sumA.sumWatchHours.toFixed(2), sumB.sumWatchHours.toFixed(2), (sumA.sumWatchHours - sumB.sumWatchHours).toFixed(2), pct(sumA.sumWatchHours, sumB.sumWatchHours)],
      ['Avg Completion %', sumA.avgCompletion.toFixed(2), sumB.avgCompletion.toFixed(2), (sumA.avgCompletion - sumB.avgCompletion).toFixed(2), pct(sumA.avgCompletion, sumB.avgCompletion)],
      ['Plays', sumA.plays, sumB.plays, sumA.plays - sumB.plays, pct(sumA.plays, sumB.plays)],
      ['Pauses', sumA.pauses, sumB.pauses, sumA.pauses - sumB.pauses, pct(sumA.pauses, sumB.pauses)],
      ['Errors', sumA.errors, sumB.errors, sumA.errors - sumB.errors, pct(sumA.errors, sumB.errors)],
    ];
    return lines.map(r => r.map(sanitize).join(',')).join('\n');
  };

  // Export current Sessions table (current page or all pages)
  const exportSessionsCSV = async () => {
    try {
      setExporting(true);
      const all = await fetchAllSessions(dateRange, tableFilters, tableSort);
      const rows = all.map(x => mapRowForCSV(x, 'Current'));
      const csv = rowsToCSV(rows);
      downloadTextFile(`player-sessions-${dateRange.startDate}-to-${dateRange.endDate}.csv`, csv);
    } catch (e) {
      console.error('Export sessions failed:', e);
    } finally {
      setExporting(false);
    }
  };

  // Export Popular Videos CSV
  const exportPopularVideosCSV = () => {
    const filtered = popularVideos.filter(v =>
      v.title.toLowerCase().includes(videoSearchQuery.toLowerCase())
    );
    const rows = filtered.map(v => ({
      Rank: v.rank,
      Video: v.title,
      Category: v.category,
      'Watch Hours': formatDuration(v.watchHours || 0),
      'Total Views': v.totalViews || 0,
      'Avg Watch Time': `${Number(v.avgWatchTime || 0).toFixed(2)} min`,
      Completion: `${Number(v.completionRate || 0).toFixed(2)}%`
    }));
    const csv = rowsToCSV(rows);
    downloadTextFile(`popular-videos-${dateRange.startDate}-to-${dateRange.endDate}.csv`, csv);
  };

  // Export comparison CSV: current range vs compare range (or previous period if not set)
  const exportSessionsComparisonCSV = async () => {
    try {
      setExporting(true);
      // Determine compare range if not provided: previous period with same length
      let cStart = compareRange.startDate;
      let cEnd = compareRange.endDate;
      if (!cStart || !cEnd) {
        const curStart = new Date(dateRange.startDate);
        const curEnd = new Date(dateRange.endDate);
        const diffDays = Math.max(0, Math.ceil((curEnd - curStart) / (1000 * 60 * 60 * 24)));
        const prevEnd = new Date(curStart);
        prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - diffDays);
        cStart = prevStart.toISOString().split('T')[0];
        cEnd = prevEnd.toISOString().split('T')[0];
      }

      const [currentAll, compareAll] = await Promise.all([
        fetchAllSessions(dateRange, tableFilters, tableSort),
        fetchAllSessions({ startDate: cStart, endDate: cEnd }, tableFilters, tableSort)
      ]);

      const labelA = `${dateRange.startDate}→${dateRange.endDate}`;
      const labelB = `${cStart}→${cEnd}`;

      const rows = [
        ...currentAll.map(x => mapRowForCSV(x, labelA)),
        ...compareAll.map(x => mapRowForCSV(x, labelB)),
      ];
      const csvMain = rowsToCSV(rows);

      const sumA = computeSummary(currentAll);
      const sumB = computeSummary(compareAll);
      const comparison = buildComparisonSection(labelA, sumA, labelB, sumB);

      const fullCSV = `${csvMain}\n\nSummary Comparison\n${comparison}`;
      downloadTextFile(`player-sessions-compare-${labelA}-vs-${labelB}.csv`, fullCSV);
    } catch (e) {
      console.error('Export comparison failed:', e);
    } finally {
      setExporting(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    // Force-refresh overview/performance/engagement/device metrics
    fetchAnalyticsData(true);
    // Also refresh summary + sessions table so the bottom grid reflects current data
    fetchSummaryData();
    fetchTableData();
    // If user is on Popular tab, refresh popular section as well
    if (activeTab === 'popular') {
      fetchPopularVideos();
      if (selectedVideo?.id) {
        fetchSingleVideoAnalytics(selectedVideo.id);
      }
      fetchWeekComparison();
    }
  };

  // Chart configurations
  const trendsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const trendsChartData = {
    labels: overviewData.trendsData.labels,
    datasets: [
      {
        label: 'Sessions',
        data: overviewData.trendsData.sessions,
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.1)',
        tension: 0.4,
      },
      {
        label: 'Watch Time (Hours)',
        data: overviewData.trendsData.watchTime,
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const deviceChartData = {
    labels: ['Desktop', 'Mobile', 'Tablet', 'Smart TV'],
    datasets: [
      {
        data: [deviceData.desktop, deviceData.mobile, deviceData.tablet, deviceData.smartTv],
        backgroundColor: [
          '#FF6B6B',
          '#4ECDC4',
          '#45B7D1',
          '#96CEB4'
        ],
        borderWidth: 0,
      },
    ],
  };

  const deviceChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 20
        }
      },
    },
  };

  // Auto-sync pending dates -> quick filter when range matches a preset
  useEffect(() => {
    const norm = (d) => (d ? new Date(new Date(d).getFullYear(), new Date(d).getMonth(), new Date(d).getDate()).getTime() : null);
    const today = new Date();
    const nToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const nStart = norm(pendingRange.startDate);
    const nEnd = norm(pendingRange.endDate);
    if (!nStart || !nEnd) return;

    const oneDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.round((nEnd - nStart) / oneDay);
    const nYesterday = nToday - oneDay;

    if (nStart === nToday && nEnd === nToday) {
      if (pendingQuickFilter !== 'today') setPendingQuickFilter('today');
      return;
    }
    if (nStart === nYesterday && nEnd === nYesterday) {
      if (pendingQuickFilter !== 'yesterday') setPendingQuickFilter('yesterday');
      return;
    }
    if (nEnd === nToday && diffDays === 7) {
      if (pendingQuickFilter !== 'last7days') setPendingQuickFilter('last7days');
      return;
    }
    if (nEnd === nToday && diffDays === 30) {
      if (pendingQuickFilter !== 'last30days') setPendingQuickFilter('last30days');
      return;
    }
    // otherwise leave the current selection as is
  }, [pendingRange.startDate, pendingRange.endDate]);

  // Initialize data on component mount
  useEffect(() => {
    // Initial load for all key analytics blocks
    fetchAnalyticsData();
    fetchSummaryData();
    fetchTableData();

    // Set up real-time updates every 30 seconds
    const interval = setInterval(() => {
      // Overview / performance / engagement / devices
      fetchAnalyticsData();
      // Sessions summary + table
      fetchSummaryData();
      fetchTableData();
      // If user is currently on Popular tab, keep its data live as well
      if (activeTab === 'popular') {
        fetchPopularVideos();
        if (selectedVideo?.id) {
          fetchSingleVideoAnalytics(selectedVideo.id);
        }
        fetchWeekComparison();
      }
    }, 1800000);

    return () => clearInterval(interval);
  }, [dateRange, activeTab, selectedVideo?.id]);

  // Ensure Popular tab does not use 'Last 7 Days' quick filter
  useEffect(() => {
    if (activeTab === 'popular' && quickFilter === 'last7days') {
      handleQuickFilter('today');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ─── Scroll-spy refs & helpers ───────────────────────────────────────────
  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'performance', label: 'Performance' },
    { key: 'engagement', label: 'Engagement' },
    { key: 'devices', label: 'Devices' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'popular', label: 'Popular' },
  ];

  const sectionRefs = {
    overview: useRef(null),
    popular: useRef(null),
    performance: useRef(null),
    engagement: useRef(null),
    devices: useRef(null),
    sessions: useRef(null),
  };
  const tabBarRef = useRef(null);

  // Smooth scroll to a section, accounting for sticky tab bar + admin navbar
  const scrollToSection = useCallback((key) => {
    const el = sectionRefs[key]?.current;
    if (!el) return;
    const tabBarHeight = tabBarRef.current?.offsetHeight || 56;
    const adminNavHeight = 64; // standard admin top-navbar height
    const offset = tabBarHeight + adminNavHeight + 8;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveTab(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IntersectionObserver – updates active tab as sections scroll into view
  useEffect(() => {
    const tabKeys = TABS.map(t => t.key);
    const observers = [];

    tabKeys.forEach((key) => {
      const el = sectionRefs[key]?.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveTab(key);
        },
        // A section becomes active when its top enters the middle third of the viewport
        { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Popular tab data when active or when sort/date changes
  useEffect(() => {
    if (activeTab === 'popular') {
      fetchPopularVideos();
      fetchWeekComparison();
      // If a video already selected, refresh its analytics for new date range
      if (selectedVideo?.id) fetchSingleVideoAnalytics(selectedVideo.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, popularSortBy, dateRange.startDate, dateRange.endDate]);

  // Refetch table when params change
  useEffect(() => {
    fetchTableData();
  }, [dateRange, tableFilters.userId, tableFilters.contentId, tableSort.sortBy, tableSort.sortDir, tableData.page, tableData.pageSize]);

  // Render Overview Tab
  const renderOverviewTab = () => (
    <Row className="mt-4">
      <Col md={8}>
        <Card className="analytics-chart-card">
          <Card.Header>
            <h5>Views & Watch Time Trends</h5>
          </Card.Header>
          <Card.Body>
            <div style={{ height: '400px' }}>
              <Line data={trendsChartData} options={trendsChartOptions} />
            </div>
          </Card.Body>
        </Card>
      </Col>
      <Col md={4}>
        <Card className="analytics-chart-card">
          <Card.Header>
            <h5>Device Distribution</h5>
          </Card.Header>
          <Card.Body>
            <div style={{ height: '400px' }}>
              <Doughnut data={deviceChartData} options={deviceChartOptions} />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // Render Performance Tab
  const renderPerformanceTab = () => {

    const getSeverityBadge = (severity) => {
      const variants = {
        'Critical': 'danger',
        'High': 'warning',
        'Medium': 'info',
        'Low': 'secondary'
      };
      return <Badge bg={variants[severity] || 'secondary'}>{severity}</Badge>;
    };

    const getStatusBadge = (status) => {
      const variants = {
        'Open': 'danger',
        'Investigating': 'warning',
        'Resolved': 'success'
      };
      return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const getTrendIcon = (trend) => {
      switch (trend) {
        case 'Increasing': return <FaArrowUp className="text-danger" />;
        case 'Decreasing': return <FaArrowDown className="text-success" />;
        default: return <span className="text-muted">→</span>;
      }
    };

    return (
      <div className="mt-4">
        <Alert variant="light" className="d-flex align-items-start">
          <FaInfoCircle className="me-2 mt-1 text-info" />
          <div>
            <div className="fw-semibold">Performance Analytics</div>
            <small className="text-muted">Monitor playback performance, errors, buffering events, and quality metrics to ensure optimal user experience.</small>
          </div>
        </Alert>

        {/* KPI Cards - Only dynamic data */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="metric-card performance-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaClock /></div>
                  <div className="metric-content ms-2">
                    <h4>{performanceData.avgLoadTime ? Number(performanceData.avgLoadTime).toFixed(2) : '0.00'}s</h4>
                    <p className="mb-0">Avg Load Time</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card performance-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaExclamationTriangle /></div>
                  <div className="metric-content ms-2">
                    <h4>{performanceData.bufferingEvents || 0}</h4>
                    <p className="mb-0">Buffering Events</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card performance-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaSync /></div>
                  <div className="metric-content ms-2">
                    <h4>{performanceData.qualityChanges || 0}</h4>
                    <p className="mb-0">Quality Changes</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card performance-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaExclamationTriangle /></div>
                  <div className="metric-content ms-2">
                    <h4>{performanceData.errorRate ? Number(performanceData.errorRate).toFixed(2) : '0.00'}%</h4>
                    <p className="mb-0">Error Rate</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Performance Incidents trend - Only dynamic data */}
        {performanceSeries && performanceSeries.length > 0 ? (
          <Card className="analytics-chart-card mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Performance Incidents Trend</h5>
                <div className="d-flex gap-2">
                  <Badge bg="danger" className="d-flex align-items-center">
                    <span className="me-1">●</span> Errors
                  </Badge>
                  <Badge bg="warning" className="d-flex align-items-center">
                    <span className="me-1">●</span> Buffering
                  </Badge>
                  <Badge bg="info" className="d-flex align-items-center">
                    <span className="me-1">●</span> Quality Changes
                  </Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ height: 320 }}>
                <Line data={{
                  labels: performanceSeries.map(d => d.date || 'N/A'),
                  datasets: [
                    {
                      label: 'Errors',
                      data: performanceSeries.map(d => d.errors || 0),
                      borderColor: 'rgb(220,38,127)',
                      backgroundColor: 'rgba(220,38,127,0.1)',
                      tension: 0.3,
                      fill: true
                    },
                    {
                      label: 'Buffering Events',
                      data: performanceSeries.map(d => d.buffering || 0),
                      borderColor: 'rgb(255,193,7)',
                      backgroundColor: 'rgba(255,193,7,0.15)',
                      tension: 0.3,
                      fill: true
                    },
                    {
                      label: 'Quality Changes',
                      data: performanceSeries.map(d => d.qualityChanges || 0),
                      borderColor: 'rgb(13,202,240)',
                      backgroundColor: 'rgba(13,202,240,0.15)',
                      tension: 0.3,
                      fill: true
                    }
                  ]
                }} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Incidents'
                      }
                    },
                    x: {
                      title: {
                        display: true,
                        text: 'Date'
                      }
                    }
                  },
                  interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                  }
                }} />
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card className="analytics-chart-card mb-4">
            <Card.Header>
              <h5 className="mb-0">Performance Incidents Trend</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center text-muted p-4">
                <FaInfoCircle className="mb-2" size={24} />
                <div>No performance trend data available</div>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Detailed Error Analysis Table */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Error Analysis</h5>
              <div className="d-flex gap-2">
                <Button variant="outline-primary" size="sm">
                  <FaDownload className="me-1" /> Export
                </Button>
                <Button variant="outline-secondary" size="sm">
                  <FaSync className="me-1" /> Refresh
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Error Code</th>
                    <th>Type/Category</th>
                    <th>Severity</th>
                    <th>Device/Platform</th>
                    <th>Browser/OS Version</th>
                    <th>First/Last Occurrence</th>
                    <th className="text-center">Users Impacted</th>
                    <th>Geography</th>
                    <th>Session ID</th>
                    <th className="text-center">Resolution Status</th>
                    <th className="text-center">% of Sessions</th>
                    <th className="text-center">Trend</th>
                    <th>Recommended Fix</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.topErrors?.length > 0 ? summary.topErrors.map((error, index) => (
                    <tr key={index}>
                      <td className="fw-bold text-danger">{error.errorCode || error.error_code || '-'}</td>
                      <td>
                        <Badge bg="outline-secondary" text="dark">{error.errorType || error.error_type}</Badge>
                      </td>
                      <td>{getSeverityBadge(error.severity || 'Medium')}</td>
                      <td className="text-nowrap">{error.devicePlatform || error.device_platform || '-'}</td>
                      <td className="text-nowrap">{error.browserVersion || error.browser_version || '-'}</td>
                      <td className="text-nowrap small">{error.timestamp ? new Date(error.timestamp).toLocaleString() : '-'}</td>
                      <td className="text-center fw-bold">{(error.userImpacted || error.users_impacted || 0).toLocaleString()}</td>
                      <td>{error.geography || error.region || '-'}</td>
                      <td className="font-monospace small">{error.sessionId || error.session_id || '-'}</td>
                      <td className="text-center">{getStatusBadge(error.resolutionStatus || error.status || 'Open')}</td>
                      <td className="text-center fw-bold">{error.percentageOfSessions || error.percentage_of_sessions || '0'}%</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                          {getTrendIcon(error.errorTrend || error.trend || 'Stable')}
                          <small className="ms-1">{error.errorTrend || error.trend || 'Stable'}</small>
                        </div>
                      </td>
                      <td>
                        <small className="text-muted" title={error.recommendedFix || error.recommended_fix || 'No recommendation'}>
                          {(error.recommendedFix || error.recommended_fix || 'No recommendation').length > 20 ?
                            `${(error.recommendedFix || error.recommended_fix || 'No recommendation').substring(0, 20)}...` :
                            (error.recommendedFix || error.recommended_fix || 'No recommendation')
                          }
                        </small>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={13} className="text-center text-muted">No error data available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Error Summary Stats */}
            <div className="mt-3 p-3 bg-light rounded">
              <Row>
                <Col md={3}>
                  <div className="text-center">
                    <div className="h5 text-danger mb-1">{summary.topErrors?.length || 0}</div>
                    <div className="small text-muted">Active Error Types</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="h5 text-warning mb-1">{summary.topErrors?.reduce((sum, e) => sum + (e.userImpacted || e.users_impacted || 0), 0).toLocaleString() || 0}</div>
                    <div className="small text-muted">Total Users Impacted</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="h5 text-info mb-1">{summary.topErrors?.filter(e => (e.resolutionStatus || e.status) === 'Resolved').length || 0}</div>
                    <div className="small text-muted">Errors Resolved</div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="h5 text-success mb-1">{Number(100 - (performanceData.errorRate || 0)).toFixed(1)}%</div>
                    <div className="small text-muted">Success Rate</div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Render Engagement Tab
  const renderEngagementTab = () => {
    const totalSessions = Number(summary.totalSessions || 0);
    const per100 = (n) => totalSessions ? Number(((n / totalSessions) * 100).toFixed(1)) : 0;

    // Sample engagement data for demonstration when no API data
    const sampleEngagementData = {
      rewatchEvents: engagementData.seekEvents || 0,
      pauseEvents: summary.pauseCount || 0,
      resumeEvents: summary.playCount || 0,
      playRate: overviewData.completionRate || 0,
      avgPercentageWatched: overviewData.completionRate || 0,
      concurrentViewerPeak: realTimeMetrics.currentViewers || 0,
      likesCount: 0,
      dislikesCount: 0,
      commentsCount: 0
    };

    // Engagement funnel data - use API data if available
    const engagementFunnelData = {
      videoStart: 100,
      quarter: overviewData.completionRate * 1.2 || 75,
      half: overviewData.completionRate * 1.1 || 60,
      threeQuarters: overviewData.completionRate * 1.05 || 50,
      completed: overviewData.completionRate || 45
    };

    // Top engagement actions - use API data if available, otherwise show empty state
    const topEngagementActions = [
      { eventType: 'Seek Forward', count: engagementData.seekEvents || 0, percentage: per100(engagementData.seekEvents || 0), avgPosition: '2:15' },
      { eventType: 'Seek Backward', count: Math.floor((engagementData.seekEvents || 0) * 0.6), percentage: per100(Math.floor((engagementData.seekEvents || 0) * 0.6)), avgPosition: '1:43' },
      { eventType: 'Pause', count: summary.pauseCount || 0, percentage: per100(summary.pauseCount || 0), avgPosition: '3:28' },
      { eventType: 'Skip Chapter', count: engagementData.skipEvents || 0, percentage: per100(engagementData.skipEvents || 0), avgPosition: '4:12' },
      { eventType: 'Replay Section', count: Math.floor((engagementData.seekEvents || 0) * 0.2), percentage: per100(Math.floor((engagementData.seekEvents || 0) * 0.2)), avgPosition: '2:56' },
      { eventType: 'Like/React', count: 0, percentage: 0, avgPosition: '6:23' },
      { eventType: 'Volume Change', count: 0, percentage: 0, avgPosition: '1:28' }
    ];

    const interactionsData = {
      labels: ['Seeks', 'Skips', 'Pauses', 'Replays', 'Likes/Reactions'],
      datasets: [{
        label: 'Per 100 sessions',
        data: [
          per100(engagementData.seekEvents || 0),
          per100(engagementData.skipEvents || 0),
          per100(summary.pauseCount || 0),
          per100(sampleEngagementData.rewatchEvents),
          per100(sampleEngagementData.likesCount)
        ],
        backgroundColor: [
          'rgba(54,162,235,0.7)',
          'rgba(255,206,86,0.7)',
          'rgba(255,99,132,0.7)',
          'rgba(75,192,192,0.7)',
          'rgba(153,102,255,0.7)'
        ],
        borderColor: [
          'rgba(54,162,235,1)',
          'rgba(255,206,86,1)',
          'rgba(255,99,132,1)',
          'rgba(75,192,192,1)',
          'rgba(153,102,255,1)'
        ],
        borderWidth: 1
      }]
    };

    const funnelData = {
      labels: ['Video Start', '25% Watched', '50% Watched', '75% Watched', 'Completed'],
      datasets: [{
        label: '% of Viewers',
        data: [engagementFunnelData.videoStart, engagementFunnelData.quarter, engagementFunnelData.half, engagementFunnelData.threeQuarters, engagementFunnelData.completed],
        backgroundColor: 'rgba(40, 167, 69, 0.6)',
        borderColor: 'rgba(40, 167, 69, 1)',
        borderWidth: 2,
        fill: true
      }]
    };

    return (
      <div className="mt-4">
        <Alert variant="light" className="d-flex align-items-start">
          <FaInfoCircle className="me-2 mt-1 text-info" />
          <div>
            <div className="fw-semibold">User Engagement Patterns</div>
            <small className="text-muted">Monitor user interactions, engagement depth, and content stickiness to optimize viewer experience and content strategy.</small>
          </div>
        </Alert>

        {/* Enhanced KPI Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="metric-card engagement-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaClock /></div>
                  <div className="metric-content ms-2">
                    <h4>{Number(engagementData.avgSessionDuration || 0).toFixed(1)} min</h4>
                    <p className="mb-0">Avg Session Duration</p>
                    <small className={`text-${engagementData.avgSessionDuration > 0 ? 'success' : 'muted'}`}>
                      {engagementData.avgSessionDuration > 0 ? '↑ Active' : 'No data'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card engagement-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaCheckCircle /></div>
                  <div className="metric-content ms-2">
                    <h4>{overviewData.completionRate || 0}%</h4>
                    <p className="mb-0">Completion Rate</p>
                    <small className={`text-${overviewData.completionRate > 0 ? 'success' : 'muted'}`}>
                      {overviewData.completionRate > 0 ? '↑ Tracked' : 'No data'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card engagement-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaSync /></div>
                  <div className="metric-content ms-2">
                    <h4>{sampleEngagementData.rewatchEvents.toLocaleString()}</h4>
                    <p className="mb-0">Rewatch Events</p>
                    <small className={`text-${sampleEngagementData.rewatchEvents > 0 ? 'info' : 'muted'}`}>
                      {sampleEngagementData.rewatchEvents > 0 ? '↑ Active' : 'No data'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card engagement-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon"><FaPlayCircle /></div>
                  <div className="metric-content ms-2">
                    <h4>{sampleEngagementData.playRate}%</h4>
                    <p className="mb-0">Play Rate</p>
                    <small className={`text-${sampleEngagementData.playRate > 0 ? 'success' : 'muted'}`}>
                      {sampleEngagementData.playRate > 0 ? '↑ Active' : 'No data'}
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Engagement Funnel & Interactions */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="analytics-chart-card">
              <Card.Header>
                <h5>Engagement Funnel</h5>
                <small className="text-muted">Viewer journey through content consumption</small>
              </Card.Header>
              <Card.Body>
                <div style={{ height: 320 }}>
                  <Line data={funnelData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${context.parsed.y}% of viewers reached this point`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        max: 100,
                        title: {
                          display: true,
                          text: 'Percentage of Viewers'
                        }
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Video Progress'
                        }
                      }
                    }
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="analytics-chart-card">
              <Card.Header><h5>User Interactions per 100 Sessions</h5></Card.Header>
              <Card.Body>
                <div style={{ height: 320 }}>
                  <Bar data={interactionsData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `${context.parsed.y} interactions per 100 sessions`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Interactions per 100 Sessions'
                        }
                      }
                    }
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Top Engagement Actions Table */}
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Top Engagement Actions</h5>
              <div className="d-flex gap-2">
                <Button variant="outline-primary" size="sm">
                  <FaDownload className="me-1" /> Export
                </Button>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Event Type</th>
                    <th className="text-center">Count</th>
                    <th className="text-center">% of Sessions</th>
                    <th className="text-center">Avg Position in Video</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topEngagementActions.some(action => action.count > 0) ? topEngagementActions.map((action, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">
                        <div className="d-flex align-items-center">
                          <div className={`me-2 ${action.eventType.includes('Seek') ? 'text-primary' :
                            action.eventType.includes('Pause') ? 'text-warning' :
                              action.eventType.includes('Skip') ? 'text-danger' :
                                action.eventType.includes('Replay') ? 'text-success' :
                                  'text-info'}`}>
                            {action.eventType.includes('Seek') ? '⏭️' :
                              action.eventType.includes('Pause') ? '⏸️' :
                                action.eventType.includes('Skip') ? '⏩' :
                                  action.eventType.includes('Replay') ? '🔄' :
                                    action.eventType.includes('Like') ? '👍' :
                                      action.eventType.includes('Volume') ? '🔊' : '▶️'}
                          </div>
                          {action.eventType}
                        </div>
                      </td>
                      <td className="text-center fw-bold">{action.count.toLocaleString()}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="flex-grow-1 me-2">
                            <ProgressBar
                              now={action.percentage}
                              style={{ height: '8px' }}
                              variant={action.percentage > 20 ? 'success' : action.percentage > 10 ? 'warning' : 'info'}
                            />
                          </div>
                          <small className="fw-bold">{action.percentage.toFixed(1)}%</small>
                        </div>
                      </td>
                      <td className="text-center font-monospace">{action.avgPosition}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center">
                          {action.count > 0 ? (
                            <><FaArrowUp className="text-success me-1" /><small>Active</small></>
                          ) : (
                            <small className="text-muted">No data</small>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="text-center text-muted p-4">
                        <FaInfoCircle className="mb-2" size={20} />
                        <div>No engagement action data available</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Engagement Heatmap */}
        <Card>
          <Card.Header>
            <h5>Engagement Heatmap</h5>
            <small className="text-muted">Shows viewer behavior patterns throughout video timeline</small>
          </Card.Header>
          <Card.Body>
            {engagementData.heatmapData && engagementData.heatmapData.length > 0 ? (
              <div className="heatmap-container p-2 bg-light rounded">
                <div className="d-flex" style={{ height: '60px' }}>
                  {engagementData.heatmapData.slice(0, 20).map((data, i) => {
                    const engagement = data.engagement || 0;
                    const color = engagement > 70 ? '#28a745' : engagement > 40 ? '#ffc107' : '#dc3545';
                    return (
                      <div
                        key={i}
                        className="flex-fill mx-1 rounded"
                        style={{
                          backgroundColor: color,
                          opacity: 0.3 + ((engagement / 100) * 0.7),
                          minWidth: '10px'
                        }}
                        title={`Time: ${data.time || i * 30}s, Engagement: ${engagement}%`}
                      />
                    );
                  })}
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-muted">Start</small>
                  <small className="text-muted">25%</small>
                  <small className="text-muted">50%</small>
                  <small className="text-muted">75%</small>
                  <small className="text-muted">End</small>
                </div>
              </div>
            ) : (
              <Row className="mb-3">
                <Col md={4}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 text-primary mb-1">{sampleEngagementData.avgPercentageWatched || 0}%</div>
                    <div className="small text-muted">Avg % of Video Watched</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 text-info mb-1">{sampleEngagementData.concurrentViewerPeak || 0}</div>
                    <div className="small text-muted">Peak Concurrent Viewers</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-center p-3 bg-light rounded">
                    <div className="h5 text-success mb-1">N/A</div>
                    <div className="small text-muted">Positive Engagement Rate</div>
                  </div>
                </Col>
              </Row>
            )}

            <div className="alert alert-info mt-3">
              <div className="d-flex align-items-start">
                <FaInfoCircle className="me-2 mt-1" />
                <div>
                  <div className="fw-semibold">Heatmap Insights</div>
                  <div className="small">
                    {engagementData.heatmapData && engagementData.heatmapData.length > 0 ? (
                      <ul className="mb-0">
                        <li><strong>Data Available:</strong> Showing engagement patterns from API data</li>
                        <li><strong>Timeline Analysis:</strong> Viewer interaction points mapped</li>
                      </ul>
                    ) : (
                      <div>No detailed engagement heatmap data available. Showing basic metrics from API when available.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Render Devices Tab
  const renderDevicesTab = () => {
    const perf = deviceData?.performance || {};

    // Sample device performance data using API data where available
    const devicePerformanceData = [
      {
        deviceType: 'Desktop',
        totalSessions: perf.desktop?.totalSessions || 0,
        avgWatchTime: perf.desktop?.avgWatchTimeMinutes || 0,
        completionRate: perf.desktop?.avgCompletionRate || overviewData.completionRate || 0,
        errorRate: perf.desktop?.errorRate || performanceData.errorRate || 0,
        bufferingRatio: 0,
        avgBitrate: 0,
        crashReports: 0,
        engagementActions: perf.desktop?.engagementActions || 0,
        geoSplit: 'Data not available',
        distribution: deviceData.desktop || 0
      },
      {
        deviceType: 'Mobile',
        totalSessions: perf.mobile?.totalSessions || 0,
        avgWatchTime: perf.mobile?.avgWatchTimeMinutes || 0,
        completionRate: perf.mobile?.avgCompletionRate || overviewData.completionRate || 0,
        errorRate: perf.mobile?.errorRate || performanceData.errorRate || 0,
        bufferingRatio: 0,
        avgBitrate: 0,
        crashReports: 0,
        engagementActions: perf.mobile?.engagementActions || 0,
        geoSplit: 'Data not available',
        distribution: deviceData.mobile || 0
      },
      {
        deviceType: 'Tablet',
        totalSessions: perf.tablet?.totalSessions || 0,
        avgWatchTime: perf.tablet?.avgWatchTimeMinutes || 0,
        completionRate: perf.tablet?.avgCompletionRate || overviewData.completionRate || 0,
        errorRate: perf.tablet?.errorRate || performanceData.errorRate || 0,
        bufferingRatio: 0,
        avgBitrate: 0,
        crashReports: 0,
        engagementActions: perf.tablet?.engagementActions || 0,
        geoSplit: 'Data not available',
        distribution: deviceData.tablet || 0
      },
      {
        deviceType: 'Smart TV',
        totalSessions: perf.smartTv?.totalSessions || 0,
        avgWatchTime: perf.smartTv?.avgWatchTimeMinutes || 0,
        completionRate: perf.smartTv?.avgCompletionRate || overviewData.completionRate || 0,
        errorRate: perf.smartTv?.errorRate || performanceData.errorRate || 0,
        bufferingRatio: 0,
        avgBitrate: 0,
        crashReports: 0,
        engagementActions: perf.smartTv?.engagementActions || 0,
        geoSplit: 'Data not available',
        distribution: deviceData.smartTv || 0
      }
    ];

    // Device performance comparison chart data
    const devicePerformanceChartData = {
      labels: devicePerformanceData.map(d => d.deviceType),
      datasets: [
        {
          label: 'Avg Watch Time (min)',
          data: devicePerformanceData.map(d => d.avgWatchTime),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Completion Rate (%)',
          data: devicePerformanceData.map(d => d.completionRate),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    const deviceErrorRateData = {
      labels: devicePerformanceData.map(d => d.deviceType),
      datasets: [{
        label: 'Error Rate (%)',
        data: devicePerformanceData.map(d => d.errorRate),
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 205, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }]
    };

    // Only use dynamic device data from API
    const hasDeviceData = deviceData && (deviceData.desktop || deviceData.mobile || deviceData.tablet || deviceData.smartTv);

    const getDeviceIcon = (deviceType) => {
      switch (deviceType.toLowerCase()) {
        case 'desktop': return '🖥️';
        case 'mobile': return '📱';
        case 'tablet': return '📱';
        case 'smart tv': return '📺';
        default: return '📱';
      }
    };

    const getPerformanceColor = (value, type) => {
      switch (type) {
        case 'completion':
          return value >= 70 ? 'success' : value >= 50 ? 'warning' : 'danger';
        case 'error':
          return value <= 1 ? 'success' : value <= 2 ? 'warning' : 'danger';
        case 'buffering':
          return value <= 0.1 ? 'success' : value <= 0.2 ? 'warning' : 'danger';
        default:
          return 'info';
      }
    };

    return (
      <div className="mt-4">
        <Alert variant="light" className="d-flex align-items-start">
          <FaInfoCircle className="me-2 mt-1 text-info" />
          <div>
            <div className="fw-semibold">Device Analytics</div>
            <small className="text-muted">Monitor device-specific performance, engagement patterns, and technical metrics to optimize user experience across platforms.</small>
          </div>
        </Alert>

        {/* Device Distribution & Performance Overview */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="analytics-chart-card h-100">
              <Card.Header>
                <h5>Device Distribution</h5>
                <small className="text-muted">Usage breakdown by device type</small>
              </Card.Header>
              <Card.Body>
                {hasDeviceData ? (
                  <div style={{ height: '320px' }}>
                    <Doughnut data={deviceChartData} options={deviceChartOptions} />
                  </div>
                ) : (
                  <div className="text-center text-muted p-4" style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <FaInfoCircle className="mb-2" size={24} />
                    <div>No device distribution data available</div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="analytics-chart-card h-100">
              <Card.Header>
                <h5>Performance by Device</h5>
                <small className="text-muted">Watch time and completion rates</small>
              </Card.Header>
              <Card.Body>
                <div style={{ height: '320px' }}>
                  <Bar data={devicePerformanceChartData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { position: 'bottom' },
                      tooltip: {
                        mode: 'index',
                        intersect: false,
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Minutes / Percentage'
                        }
                      }
                    }
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Detailed Device Metrics Table */}
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Device Performance Metrics</h5>
              {/* <div className="d-flex gap-2">
                <Button variant="outline-primary" size="sm">
                  <FaDownload className="me-1" /> Export
                </Button>
                <Button variant="outline-secondary" size="sm">
                  <FaSync className="me-1" /> Refresh
                </Button>
              </div> */}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Device Type</th>
                    <th className="text-center">Total Sessions</th>
                    <th className="text-center">Avg Watch Time</th>
                    <th className="text-center">Completion Rate</th>
                    <th className="text-center">Error Rate</th>
                    <th className="text-center">Buffering Ratio</th>
                    <th className="text-center">Avg Bitrate</th>
                    <th className="text-center">Crash Reports</th>
                    <th className="text-center">Engagement Actions</th>
                    <th className="text-center">Top Regions</th>
                  </tr>
                </thead>
                <tbody>
                  {devicePerformanceData.some(d => d.totalSessions > 0 || d.distribution > 0) ? devicePerformanceData.map((device, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">
                        <div className="d-flex align-items-center">
                          <span className="me-2">{getDeviceIcon(device.deviceType)}</span>
                          <div>
                            <div>{device.deviceType}</div>
                            <small className="text-muted">{device.distribution}% of total</small>
                          </div>
                        </div>
                      </td>
                      <td className="text-center fw-bold">{device.totalSessions.toLocaleString()}</td>
                      <td className="text-center">
                        <div className="fw-bold">{device.avgWatchTime || 0} min</div>
                        {!device.avgWatchTime && <small className="text-muted">No data</small>}
                      </td>
                      <td className="text-center">
                        <Badge bg={getPerformanceColor(device.completionRate, 'completion')}>
                          {device.completionRate || 0}%
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg={getPerformanceColor(device.errorRate, 'error')}>
                          {device.errorRate ? Number(device.errorRate).toFixed(1) : '0.0'}%
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">
                          {device.bufferingRatio ? (device.bufferingRatio * 100).toFixed(1) : '0.0'}%
                        </Badge>
                      </td>
                      <td className="text-center font-monospace">
                        <div className="fw-bold">{device.avgBitrate || 0} kbps</div>
                        {!device.avgBitrate && <small className="text-muted">No data</small>}
                      </td>
                      <td className="text-center">
                        <div className="fw-bold text-muted">
                          {device.crashReports || 0}
                        </div>
                        <small className="text-muted">reports</small>
                      </td>
                      <td className="text-center">
                        <div className="fw-bold text-primary">{device.engagementActions.toLocaleString()}</div>
                        <small className="text-muted">interactions</small>
                      </td>
                      <td className="text-center">
                        <small className="text-muted">{device.geoSplit}</small>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={10} className="text-center text-muted p-4">
                        <FaInfoCircle className="mb-2" size={20} />
                        <div>No device performance data available</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Error Rate Comparison */}
        <Row className="mb-4">
          <Col md={6}>
            <Card className="analytics-chart-card">
              <Card.Header>
                <h5>Error Rate by Device</h5>
                <small className="text-muted">Device-specific error analysis</small>
              </Card.Header>
              <Card.Body>
                <div style={{ height: '300px' }}>
                  <Bar data={deviceErrorRateData} options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            return `Error Rate: ${context.parsed.y}%`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Error Rate (%)'
                        }
                      }
                    }
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="analytics-chart-card">
              <Card.Header>
                <h5>Device Performance Summary</h5>
                <small className="text-muted">Key performance indicators</small>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h4 text-primary mb-1">{devicePerformanceData.reduce((sum, d) => sum + d.totalSessions, 0).toLocaleString()}</div>
                      <div className="small text-muted">Total Sessions</div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h4 text-success mb-1">{(devicePerformanceData.reduce((sum, d) => sum + d.avgWatchTime, 0) / devicePerformanceData.length).toFixed(1)} min</div>
                      <div className="small text-muted">Avg Watch Time</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h4 text-info mb-1">{(devicePerformanceData.reduce((sum, d) => sum + d.completionRate, 0) / devicePerformanceData.length).toFixed(1)}%</div>
                      <div className="small text-muted">Avg Completion Rate</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h4 text-warning mb-1">{(devicePerformanceData.reduce((sum, d) => sum + d.errorRate, 0) / devicePerformanceData.length).toFixed(1)}%</div>
                      <div className="small text-muted">Avg Error Rate</div>
                    </div>
                  </Col>
                </Row>

                {/* Device Recommendations */}
                <div className="mt-3">
                  <div className="alert alert-info">
                    <div className="fw-semibold mb-2">📊 Device Insights</div>
                    <div className="small">
                      {hasDeviceData ? (
                        <ul className="mb-0">
                          <li><strong>Data Available:</strong> Showing device distribution from API</li>
                          <li><strong>Performance:</strong> Metrics calculated from available analytics data</li>
                        </ul>
                      ) : (
                        <div>No detailed device performance data available. Configure analytics endpoints to see device-specific metrics.</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </div>
    );
  };

  // Render Popular Tab (new)
  const renderPopularTab = () => {
    const last7Labels = overviewData.trendsData?.labels || [];
    const filteredPopularVideos = popularVideos.filter(v =>
      v.title.toLowerCase().includes(videoSearchQuery.toLowerCase())
    );
    const last7Values = overviewData.trendsData?.watchTime || [];

    // Pagination for Popular Videos
    const totalPopularPages = Math.max(1, Math.ceil(filteredPopularVideos.length / popularPageSize));
    const startIndex = (popularPage - 1) * popularPageSize;
    const paginatedPopularVideos = filteredPopularVideos.slice(startIndex, startIndex + popularPageSize);

    const last7ChartData = {
      labels: last7Labels,
      datasets: [{
        label: 'Watch Hours',
        data: last7Values,
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.1)',
        tension: 0.4,
        fill: true,
      }]
    };

    const last7ChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: { y: { beginAtZero: true } }
    };

    return (
      <div className="mt-4">
        {/* Single Video Watch Hours */}
        <Card className="mb-4">
          <Card.Header><h5>Single Video Watch Hours</h5></Card.Header>
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md={6}>
                <Form.Label>Select a Video</Form.Label>
                <div className="position-relative">
                  <div className="input-group">
                    <span className="input-group-text"><FaSearch /></span>
                    <input
                      className="form-control"
                      placeholder="Search video title..."
                      value={videoSearchQuery}
                      onChange={(e) => { setVideoSearchQuery(e.target.value); searchVideos(e.target.value); }}
                      onFocus={() => setShowVideoDropdown(true)}
                    />
                  </div>
                  {showVideoDropdown && videoSearchResults.length > 0 && (
                    <div className="dropdown-menu show w-100" style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {videoSearchResults.map(v => (
                        <button key={v.id} className="dropdown-item d-flex align-items-center" type="button" onClick={() => { setSelectedVideo(v); setVideoSearchQuery(v.title); setShowVideoDropdown(false); fetchSingleVideoAnalytics(v.id); }}>
                          {/* <img src={v.thumbnail} alt="thumb" className="me-2" style={{ width: 48, height: 28, objectFit: 'cover', borderRadius: 4 }} /> */}
                          <div className="text-start">
                            <div className="fw-semibold">{v.title}</div>
                            <small className="text-muted">{v.category}</small>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {singleVideoAnalytics && (
              <>
                <Row className="mt-3">
                  <Col md={3}>
                    <Card className="metric-card info-card"><Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="metric-icon info"><FaClock /></div>
                        <div className="metric-content">
                          <h3>{formatDuration(singleVideoAnalytics.totalWatchHours)}</h3>
                          <p>Total Watch Hours</p>
                        </div>
                      </div>
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card className="metric-card primary-card"><Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="metric-icon primary"><FaUser /></div>
                        <div className="metric-content">
                          <h3>{singleVideoAnalytics.uniqueViewers.toLocaleString()}</h3>
                          <p>Unique Viewers</p>
                        </div>
                      </div>
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card className="metric-card success-card"><Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="metric-icon success"><FaPlay /></div>
                        <div className="metric-content">
                          <h3>{formatDuration(singleVideoAnalytics.avgWatchDuration / 60)}</h3>
                          <p>Avg Watch Time</p>
                        </div>
                      </div>
                    </Card.Body></Card>
                  </Col>
                  <Col md={3}>
                    <Card className="metric-card warning-card"><Card.Body>
                      <div className="d-flex align-items-center">
                        <div className="metric-icon warning"><FaList /></div>
                        <div className="metric-content">
                          <h3>{singleVideoAnalytics.avgCompletion}%</h3>
                          <p>Completion Rate</p>
                        </div>
                      </div>
                    </Card.Body></Card>
                  </Col>
                </Row>

                {/* Daily watch hours trend for the selected video */}
                <Card className="mt-3">
                  <Card.Header><h6>Daily Watch Hours</h6></Card.Header>
                  <Card.Body>
                    <div style={{ height: 300 }}>
                      <Line
                        data={{
                          labels: singleVideoAnalytics.daily.map(d => new Date(d.date || d.day || d.ts).toLocaleDateString()),
                          datasets: [{
                            label: 'Watch Hours',
                            data: singleVideoAnalytics.daily.map(d => d.watch_hours || d.watchHours || 0),
                            borderColor: 'rgb(75, 192, 192)',
                            backgroundColor: 'rgba(75, 192, 192, 0.2)',
                            tension: 0.4,
                            fill: true
                          }]
                        }}
                        options={last7ChartOptions}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Watch Hours Trend */}
        <Card className="mb-4">
          <Card.Header><h5>Watch Hours Trend</h5></Card.Header>
          <Card.Body>
            <div style={{ height: 320 }}>
              <Line
                data={{
                  labels: overviewData.trendsData?.labels || [],
                  datasets: [{
                    label: 'Watch Hours',
                    data: overviewData.trendsData?.watchTime || [],
                    borderColor: 'rgb(53, 162, 235)',
                    backgroundColor: 'rgba(53, 162, 235, 0.1)',
                    tension: 0.4,
                    fill: true,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: { y: { beginAtZero: true } }
                }}
              />
            </div>
          </Card.Body>
        </Card>

        {/* Ranking table */}
        <Card>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Popular Videos</h5>
              <div className="d-flex gap-2">
                <Button variant="outline-success" size="sm" onClick={exportPopularVideosCSV}>
                  <FaDownload className="me-1" /> Export CSV
                </Button>
                <Form.Select style={{ width: 220 }} value={popularSortBy} onChange={(e) => { setPopularSortBy(e.target.value); setPopularPage(1); }}>
                  <option value="watchHours">Most Watch Hours</option>
                  <option value="viewers">Most Viewers</option>
                  <option value="completionRate">Highest Completion</option>
                  <option value="trending">Trending Now</option>
                </Form.Select>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {popularLoading ? (
              <div>Loading popular videos...</div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Video</th>
                        <th>Watch Hours</th>
                        <th>Total Views</th>
                        {/* <th>Viewers</th> */}
                        <th>Avg Watch Time</th>
                        <th>Completion</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPopularVideos.length === 0 ? (
                        <tr><td colSpan={8} className="text-center text-muted">No data</td></tr>
                      ) : paginatedPopularVideos.map((v) => (
                        <tr key={v.id}>
                          <td className="text-center">{v.rank <= 3 ? <FaTrophy className={v.rank === 1 ? 'text-warning' : v.rank === 2 ? 'text-secondary' : 'text-brown'} /> : `#${v.rank}`}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div>
                                <div className="fw-semibold">{v.title}</div>
                                <small className="text-muted">{v.category}</small>
                              </div>
                            </div>
                          </td>
                          <td><strong>{formatDuration(v.watchHours || 0)}</strong></td>
                          <td><strong>{(v.totalViews || 0).toLocaleString()}</strong></td>
                          {/* <td>{(v.viewers || 0).toLocaleString()}</td> */}
                          <td>{Number(v.avgWatchTime || 0).toFixed(2)} min</td>
                          <td>{Number(v.completionRate || 0).toFixed(2)}%</td>
                          <td>
                            <Button variant="link" size="sm" onClick={() => handleShowDetails(v)}>
                              <FaChartLine className="me-1" /> Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination for Popular Videos */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    Showing {filteredPopularVideos.length === 0 ? 0 : startIndex + 1} - {Math.min(popularPage * popularPageSize, filteredPopularVideos.length)} of {filteredPopularVideos.length} videos
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={popularPage <= 1}
                      onClick={() => setPopularPage(1)}
                      title="First Page"
                    >
                      &laquo;
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={popularPage <= 1}
                      onClick={() => setPopularPage(prev => Math.max(1, prev - 1))}
                      title="Previous Page"
                    >
                      &lsaquo; Prev
                    </Button>
                    <div className="d-flex align-items-center gap-1 mx-2">
                      <span className="small text-muted">Page</span>
                      <Form.Control
                        type="number"
                        size="sm"
                        min={1}
                        max={totalPopularPages}
                        value={popularPage}
                        onChange={(e) => {
                          const newPage = parseInt(e.target.value, 10) || 1;
                          if (newPage >= 1 && newPage <= totalPopularPages) {
                            setPopularPage(newPage);
                          }
                        }}
                        style={{ width: '70px' }}
                      />
                      <span className="small text-muted">of {totalPopularPages}</span>
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={popularPage >= totalPopularPages}
                      onClick={() => setPopularPage(prev => Math.min(totalPopularPages, prev + 1))}
                      title="Next Page"
                    >
                      Next &rsaquo;
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={popularPage >= totalPopularPages}
                      onClick={() => setPopularPage(totalPopularPages)}
                      title="Last Page"
                    >
                      &raquo;
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Render Sessions Tab (new) - Enhanced version
  const renderSessionsTab = () => {
    const totalPages = Math.max(1, Math.ceil((tableData.total || 0) / (tableData.pageSize || 10)));
    const formatDate = (d) => d ? new Date(d).toLocaleString() : '-';
    const sortIcon = (col) => {
      if (tableSort.sortBy !== col) return <span className="text-muted ms-1">↕</span>;
      return tableSort.sortDir === 'ASC' ?
        <FaArrowUp className="text-primary ms-1" size={12} /> :
        <FaArrowDown className="text-primary ms-1" size={12} />;
    };
    const handleSort = (col) => {
      setTableSort((prev) => ({
        sortBy: col,
        sortDir: prev.sortBy === col && prev.sortDir === 'ASC' ? 'DESC' : 'ASC',
      }));
      setTableData((prev) => ({ ...prev, page: 1 }));
    };

    const handleFilterChange = (field, value) => {
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setTableFilters((prev) => ({ ...prev, [field]: value }));
        setTableData((prev) => ({ ...prev, page: 1 }));
      }, 500);
      setDebounceTimer(timer);
    };

    // Calculate sessions summary
    const sessionsStats = {
      totalSessions: tableData.total || 0,
      totalWatchHours: tableData.items ? tableData.items.reduce((sum, item) => sum + Number(item.watch_hours || 0), 0) : 0,
      avgCompletionRate: tableData.items && tableData.items.length > 0 ?
        tableData.items.reduce((sum, item) => sum + Number(item.completion_rate || 0), 0) / tableData.items.length : 0,
      totalPlays: tableData.items ? tableData.items.reduce((sum, item) => sum + Number(item.play_count || 0), 0) : 0,
      totalErrors: tableData.items ? tableData.items.reduce((sum, item) => sum + Number(item.error_count || 0), 0) : 0
    };

    return (
      <div className="mt-4">
        {/* Summary row */}
        <Row className="mb-3">
          <Col md={3}>
            <Card className="metric-card info-card">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon info">
                    <FaUser />
                  </div>
                  <div className="metric-content">
                    <h3>{summary.totalUsers.toLocaleString()}</h3>
                    <p>Total Users</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card primary-card">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon primary">
                    <FaPlay />
                  </div>
                  <div className="metric-content">
                    <h3>{summary.playCount.toLocaleString()}</h3>
                    <p>Play Events</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card success-card">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon success">
                    <FaPause />
                  </div>
                  <div className="metric-content">
                    <h3>{summary.pauseCount.toLocaleString()}</h3>
                    <p>Pause Events</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="metric-card warning-card">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="metric-icon warning">
                    <FaExclamationTriangle />
                  </div>
                  <div className="metric-content">
                    <h3>{summary.errorCount.toLocaleString()}</h3>
                    <p>Error Events</p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Sessions Summary */}
        <Card className="mb-3">
          <Card.Body>
            <Alert variant="light" className="d-flex align-items-start mb-3">
              <FaInfoCircle className="me-2 mt-1 text-info" />
              <div>
                <div className="fw-semibold">Sessions Overview</div>
                <small className="text-muted">Detailed session tracking showing user interactions, watch patterns, and performance metrics for comprehensive analytics.</small>
              </div>
            </Alert>
            <Row className="mb-3">
              <Col md={3}>
                <div className="session-summary-item">
                  <div className="summary-value text-primary fw-bold">{sessionsStats.totalSessions.toLocaleString()}</div>
                  <div className="summary-label">Total Sessions</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="session-summary-item">
                  <div className="summary-value text-success fw-bold">{sessionsStats.totalWatchHours.toFixed(1)}h</div>
                  <div className="summary-label">Total Watch Hours</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="session-summary-item">
                  <div className="summary-value text-info fw-bold">{sessionsStats.avgCompletionRate.toFixed(1)}%</div>
                  <div className="summary-label">Avg Completion Rate</div>
                </div>
              </Col>
              <Col md={3}>
                <div className="session-summary-item">
                  <div className="summary-value text-warning fw-bold">{sessionsStats.totalPlays.toLocaleString()}</div>
                  <div className="summary-label">Total Play Events</div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Filters */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    <FaUser className="me-1" />User ID
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by User ID..."
                    defaultValue={tableFilters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                  />
                </Form.Group>
              </Col>
              {/* <Col md={3}>
                <Form.Group>
                  <Form.Label>
                    <FaPlayCircle className="me-1" />Content ID
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by Content ID..."
                    defaultValue={tableFilters.contentId}
                    onChange={(e) => handleFilterChange('contentId', e.target.value)}
                  />
                </Form.Group>
              </Col> */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Page Size</Form.Label>
                  <Form.Select
                    value={tableData.pageSize}
                    onChange={(e) => setTableData((prev) => ({ ...prev, page: 1, pageSize: parseInt(e.target.value, 10) }))}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} className="text-end">
                <div className="text-muted d-flex align-items-center justify-content-end">
                  <div className="me-3">
                    <strong>Total:</strong> {tableData.total.toLocaleString()} sessions
                  </div>
                  {tableLoading && (
                    <div className="me-2">
                      <FaSync className="fa-spin text-primary" />
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {/* Comparison + Export controls */}
            <Row className="g-3 align-items-end mt-2">
              <Col md={3}>
                <Form.Check
                  type="switch"
                  id="compare-switch"
                  label="Compare with another period"
                  checked={compareEnabled}
                  onChange={(e) => setCompareEnabled(e.target.checked)}
                />
              </Col>
              {compareEnabled && (
                <>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Compare Start</Form.Label>
                      <Form.Control
                        type="date"
                        value={compareRange.startDate}
                        onChange={(e) => setCompareRange(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Compare End</Form.Label>
                      <Form.Control
                        type="date"
                        value={compareRange.endDate}
                        onChange={(e) => setCompareRange(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2">
                <Button variant="success" size="sm" disabled={exporting} onClick={exportSessionsCSV}>
                  <FaDownload className="me-2" /> Export CSV
                </Button>
                {/* <Button variant="outline-success" size="sm" disabled={!compareEnabled || exporting} onClick={exportSessionsComparisonCSV}>
                  <FaDownload className="me-2" /> Export Comparison CSV
                </Button> */}
              </div>
              <div className="text-muted small">{exporting ? 'Preparing export…' : ''}</div>
            </div>
            <div className="table-responsive analytics-table">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th className="text-nowrap shrink-col" style={{ cursor: 'pointer' }} onClick={() => handleSort('created_at')}>Created At{sortIcon('created_at')}</th>
                    <th className="text-nowrap">Session ID</th>
                    <th className="text-nowrap shrink-col" style={{ cursor: 'pointer' }} onClick={() => handleSort('started_at')}>Started{sortIcon('started_at')}</th>
                    <th className="text-nowrap shrink-col" style={{ cursor: 'pointer' }} onClick={() => handleSort('ended_at')}>Ended{sortIcon('ended_at')}</th>
                    <th className="text-nowrap">User ID</th>
                    <th className="text-nowrap">Role</th>
                    <th className="text-nowrap">Country</th>
                    <th className="text-nowrap">Video ID</th>
                    <th className="text-nowrap">Title</th>
                    <th className="text-nowrap">Source</th>
                    <th className="text-end text-nowrap" style={{ cursor: 'pointer' }} onClick={() => handleSort('total_watch_time_ms')}>Watch Time (min){sortIcon('total_watch_time_ms')}</th>
                    <th className="text-end text-nowrap">Completion %</th>
                    <th className="text-end text-nowrap">Play</th>
                    <th className="text-end text-nowrap">Pause</th>
                    <th className="text-end text-nowrap">Errors</th>
                    <th className="text-end text-nowrap">Error Rate %</th>
                    <th className="text-nowrap">Device</th>
                    <th className="text-end text-nowrap">Buffering</th>
                    <th className="text-end text-nowrap">Avg Quality</th>
                    <th className="text-end text-nowrap">Crashes</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <tr>
                      <td colSpan={14}>Loading...</td>
                    </tr>
                  ) : tableData.items.length === 0 ? (
                    <tr>
                      <td colSpan={14}>No sessions found</td>
                    </tr>
                  ) : (
                    tableData.items.map((item) => (
                      <tr key={item.session_id}>
                        <td className="text-nowrap">{formatDate(item.created_at)}</td>
                        <td className="text-nowrap">{item.session_id}</td>
                        <td className="text-nowrap">{formatDate(item.started_at)}</td>
                        <td className="text-nowrap">{formatDate(item.ended_at)}</td>
                        <td className="text-nowrap">{item.display_user_id || item.user_id || '-'}</td>
                        <td className="text-nowrap">{item.role || (item.user_id ? 'user' : 'guest')}</td>
                        <td className="text-nowrap">{item.country || '-'}</td>
                        <td className="text-nowrap">{item.video_id || item.video?.id || '-'}</td>
                        <td className="text-nowrap" title={item.video_title || ''}>{item.video_title || '-'}</td>
                        <td className="text-nowrap">{item.video_source || item.video?.type || '-'}</td>
                        <td className="text-end">{Math.round((Number(item.watch_hours || 0)) * 60)}</td>
                        <td className="text-end">{Number(item.completion_rate || item.completion_percentage || 0).toFixed(2)}</td>
                        <td className="text-end">{item.play_count}</td>
                        <td className="text-end">{item.pause_count}</td>
                        <td className="text-end">{item.error_count}</td>
                        <td className="text-end">{(() => {
                          const plays = Number(item.play_count || 0);
                          const errors = Number(item.error_count || 0);
                          if (plays > 0) return Math.min(100, Math.round((errors / plays) * 100));
                          return errors > 0 ? 100 : 0;
                        })()}</td>
                        <td className="text-nowrap">{item.device_summary || item.device?.deviceType || item.device?.type}</td>
                        <td className="text-end">{Number(item.buffering_count || 0)}</td>
                        <td className="text-end">{item.avg_quality ? `${item.avg_quality}p` : '-'}</td>
                        <td className="text-end">{Number(item.crash_count != null ? item.crash_count : (item.error_count || 0))}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Enhanced Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-3">
                <div className="small text-muted">
                  Showing {((tableData.page - 1) * tableData.pageSize) + 1} - {Math.min(tableData.page * tableData.pageSize, tableData.total)} of {tableData.total.toLocaleString()} sessions
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {/* First/Last page buttons */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={tableData.page <= 1 || tableLoading}
                  onClick={() => setTableData((prev) => ({ ...prev, page: 1 }))}
                  title="First Page"
                >
                  &laquo;
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={tableData.page <= 1 || tableLoading}
                  onClick={() => setTableData((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  title="Previous Page"
                >
                  &lsaquo; Prev
                </Button>

                {/* Page input */}
                <div className="d-flex align-items-center gap-1 mx-2">
                  <span className="small text-muted">Page</span>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={totalPages}
                    value={tableData.page}
                    onChange={(e) => {
                      const newPage = parseInt(e.target.value, 10) || 1;
                      if (newPage >= 1 && newPage <= totalPages) {
                        setTableData((prev) => ({ ...prev, page: newPage }));
                      }
                    }}
                    style={{ width: '70px' }}
                    disabled={tableLoading}
                  />
                  <span className="small text-muted">of {totalPages}</span>
                </div>

                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={tableData.page >= totalPages || tableLoading}
                  onClick={() => setTableData((prev) => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                  title="Next Page"
                >
                  Next &rsaquo;
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={tableData.page >= totalPages || tableLoading}
                  onClick={() => setTableData((prev) => ({ ...prev, page: totalPages }))}
                  title="Last Page"
                >
                  &raquo;
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };
  const renderVideoDetailsModal = () => {
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          titleFont: { size: 14 },
          bodyFont: { size: 13 },
          displayColors: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
          ticks: { font: { size: 11 } }
        },
        x: {
          grid: { display: false },
          ticks: { font: { size: 11 } }
        }
      }
    };

    const viewers = modalVideoData?.viewers || [];
    const totalModalPages = Math.max(1, Math.ceil(viewers.length / modalPageSize));
    const startModalIndex = (modalPage - 1) * modalPageSize;
    const paginatedViewers = viewers.slice(startModalIndex, startModalIndex + modalPageSize);

    return (
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="video-details-modal p-3">
        <Modal.Header closeButton className="border-0 bg-light">
          <Modal.Title className="h5 fw-bold mb-0">
            <FaChartLine className="me-2 text-primary" />
            Advanced Video Performance Report (Lifetime)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: '#f8fafc' }}>
          {modalLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
              <div className="text-muted fw-medium">Analyzing content metrics...</div>
            </div>
          ) : modalVideoData ? (
            <>
              <div className="d-flex align-items-center mb-4 p-3 bg-white rounded-3 shadow-sm">
                <div className="flex-grow-1">
                  <h4 className="mb-1 fw-bold text-dark">{modalVideoData.video?.title}</h4>
                  <div className="d-flex gap-2">
                    {/* <Badge bg="primary" className="rounded-pill px-3 py-2 text-uppercase" style={{ fontSize: '10px' }}>
                      {modalVideoData.video?.type || 'Content'}
                    </Badge> */}
                    <Badge bg="primary" className="rounded-pill px-3 py-2 text-uppercase" style={{ fontSize: '10px' }}>
                      {modalVideoData.video?.source || 'Source'}
                    </Badge>
                  </div>
                </div>
                <div className="text-end">
                  <small className="text-muted d-block text-uppercase fw-bold" style={{ fontSize: '10px' }}>Reporting Period</small>
                  <span className="fw-bold text-dark text-uppercase">All Time (Lifetime)</span>
                </div>
              </div>

              <Row className="g-3 mb-4">
                {[
                  { label: 'Watch Hours', val: `${modalVideoData.metrics?.totalWatchHours}h`, icon: <FaClock />, color: 'primary' },
                  { label: 'Total Views', val: modalVideoData.metrics?.totalViews?.toLocaleString(), icon: <FaPlay />, color: 'success' },
                  { label: 'Unique Viewers', val: modalVideoData.metrics?.uniqueViewers?.toLocaleString(), icon: <FaUser />, color: 'info' },
                  { label: 'Avg Completion', val: `${modalVideoData.metrics?.avgCompletion}%`, icon: <FaCheckCircle />, color: 'warning' }
                ].map((m, idx) => (
                  <Col md={3} key={idx}>
                    <Card className="h-100 border-0 shadow-sm text-center">
                      <Card.Body className="p-3">
                        <div className={`text-${m.color} mb-2`} style={{ fontSize: '20px' }}>{m.icon}</div>
                        <div className="small text-muted fw-bold text-uppercase mb-1" style={{ fontSize: '10px' }}>{m.label}</div>
                        <h4 className="mb-0 fw-bold">{m.val}</h4>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Card className="border-0 shadow-sm overflow-hidden">
                <Card.Header className="bg-white py-3 border-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold">Performance Trend</h6>
                    <small className="text-muted">Daily Watch Hours</small>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ height: 320, padding: '0 15px 15px' }}>
                    <Line
                      data={{
                        labels: modalVideoData.dailyTrend?.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) || [],
                        datasets: [{
                          label: 'Watch Hours',
                          data: modalVideoData.dailyTrend?.map(d => Number(d.watchHours)) || [],
                          borderColor: '#3b82f6',
                          backgroundColor: 'rgba(59, 130, 246, 0.08)',
                          tension: 0.4,
                          fill: true,
                          pointRadius: 4,
                          pointHoverRadius: 6,
                          pointBackgroundColor: '#fff',
                          pointBorderWidth: 2,
                          pointBorderColor: '#3b82f6'
                        }]
                      }}
                      options={chartOptions}
                    />
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm mt-4">
                <Card.Header className="bg-white py-3 border-0">
                  <h6 className="mb-0 fw-bold">Top Viewers (By Watch Time)</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="align-middle mb-0" style={{ fontSize: '13px' }}>
                      <thead className="bg-light">
                        <tr>
                          <th>User</th>
                          <th>Email</th>
                          <th className="text-center">Sessions</th>
                          <th className="text-end">Watch Hours</th>
                          <th className="text-end">Last Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedViewers.length > 0 ? (
                          paginatedViewers.map((v, i) => (
                            <tr key={i}>
                              <td>
                                <div className="fw-bold text-dark">{v.name || v.username}</div>
                                <small className="text-muted">ID: {v.userId}</small>
                              </td>
                              <td className="text-muted">{v.email}</td>
                              <td className="text-center">
                                <Badge bg="light" text="dark">{v.sessions}</Badge>
                              </td>
                              <td className="text-end fw-bold text-primary">{v.watchHours}h</td>
                              <td className="text-end text-muted">
                                {v.lastWatched ? new Date(v.lastWatched).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={5} className="text-center py-3 text-muted">No specific user data available</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination for Modal Viewers */}
                  <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
                    <div className="small text-muted">
                      Showing {viewers.length === 0 ? 0 : startModalIndex + 1} - {Math.min(modalPage * modalPageSize, viewers.length)} of {viewers.length} viewers
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={modalPage <= 1}
                        onClick={() => setModalPage(1)}
                        title="First Page"
                      >
                        &laquo;
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={modalPage <= 1}
                        onClick={() => setModalPage(prev => Math.max(1, prev - 1))}
                        title="Previous Page"
                      >
                        &lsaquo; Prev
                      </Button>
                      <div className="d-flex align-items-center gap-1 mx-2">
                        <span className="small text-muted">Page</span>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={1}
                          max={totalModalPages}
                          value={modalPage}
                          onChange={(e) => {
                            const newPage = parseInt(e.target.value, 10) || 1;
                            if (newPage >= 1 && newPage <= totalModalPages) {
                              setModalPage(newPage);
                            }
                          }}
                          style={{ width: '60px' }}
                        />
                        <span className="small text-muted">of {totalModalPages}</span>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={modalPage >= totalModalPages}
                        onClick={() => setModalPage(prev => Math.min(totalModalPages, prev + 1))}
                        title="Next Page"
                      >
                        Next &rsaquo;
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={modalPage >= totalModalPages}
                        onClick={() => setModalPage(totalModalPages)}
                        title="Last Page"
                      >
                        &raquo;
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div className="text-center py-5">
              <FaInfoCircle className="text-muted mb-3" style={{ fontSize: '48px' }} />
              <h5 className="text-dark">No Data Available</h5>
              <p className="text-muted">We couldn't find any performance metrics for this video in the selected date range.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-white p-3">
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => setShowDetailsModal(false)}>Close Report</Button>
          <Button variant="success" className="rounded-pill px-4 shadow-sm" onClick={() => {
            const headers = ["User ID", "Name", "Email", "Watch Hours", "Sessions", "Last Watched"];
            const rows = modalVideoData.viewers?.map(v => [
              v.userId,
              v.name,
              v.email,
              v.watchHours,
              v.sessions,
              v.lastWatched ? new Date(v.lastWatched).toLocaleString() : '-'
            ]) || [];

            // Build Summary Section
            const summaryLines = [
              ["Video Title", `"${modalVideoData.video?.title || ""}"`],
              ["Total Watch Hours", `"${modalVideoData.metrics?.totalWatchHours || "0"}"`],
              ["Unique Viewers", `"${modalVideoData.metrics?.uniqueViewers || "0"}"`],
              ["Avg Watch Time", `"${modalVideoData.metrics?.avgWatchTime || "0"} min"`],
              ["Completion Rate", `"${modalVideoData.metrics?.avgCompletion || "0"}%"`],
              [], // Empty line separator
              ["Viewer Details"] // Section header
            ];

            const csvContent = "data:text/csv;charset=utf-8,"
              + summaryLines.map(line => line.join(",")).join("\n") + "\n"
              + [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${modalVideoData.video?.title || "video"}_report.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}>
            <FaDownload className="me-2" /> Download CSV
          </Button>
          {/* <Button variant="primary" className="rounded-pill px-4 shadow-sm" onClick={() => window.print()}>
            <FaChartLine className="me-2" /> Print PDF
          </Button> */}
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <div className="player-analytics-page min-vh-100"
    // style={{ background: "linear-gradient(135deg, #f3f6ff 0%, #f9fbff 40%, #ffffff 100%)" }}
    >
      <Container fluid className="py-4">
        {/* Top header bar */}
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <div className="d-flex align-items-center mb-2">
              <div className="rounded-3 d-inline-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, background: "linear-gradient(135deg, #2563eb, #4f46e5)", boxShadow: "0 10px 25px rgba(37,99,235,0.35)" }}>
                <FaPlay className="text-white" />
              </div>
              <div>
                <h2 className="mb-1 fw-semibold" style={{ letterSpacing: "0.01em" }}>Player Analytics Dashboard</h2>
                <small className="text-muted">Monitor sessions, engagement and device performance in real time.</small>
              </div>
            </div>
            <div className="d-flex align-items-center small text-muted">
              <span className="badge bg-success-subtle text-success me-2 rounded-pill px-3">Live</span>
              <FaSync className="me-1" />
              Updated: {lastUpdated.toLocaleString()}
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="rounded-pill px-3"
            >
              <FaSync className={`me-2 ${loading ? 'fa-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Summary cards row */}
        <Row className="g-3 mb-4">
          <Col md={3} sm={6}>
            <Card className="border-0 h-100" style={{ boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted text-uppercase fw-semibold">Total Sessions</small>
                    <h3 className="mt-2 mb-0">{overviewData.totalSessions.toLocaleString()}</h3>
                    <small className="text-success">+ Live sessions tracking</small>
                  </div>
                  <span className="rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: "rgba(37, 99, 235, 0.08)", color: "#2563eb" }}>
                    <FaPlay />
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 h-100" style={{ boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted text-uppercase fw-semibold">Watch Time</small>
                    <h3 className="mt-2 mb-0">{overviewData.totalWatchTime}</h3>
                    <small className="text-muted">Across all player sessions</small>
                  </div>
                  <span className="rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: "rgba(22, 163, 74, 0.08)", color: "#16a34a" }}>
                    <FaClock />
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 h-100" style={{ boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted text-uppercase fw-semibold">Completion Rate</small>
                    <h3 className="mt-2 mb-0">{overviewData.completionRate}%</h3>
                    <small className="text-muted">Average per viewing session</small>
                  </div>
                  <span className="rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: "rgba(56, 189, 248, 0.08)", color: "#0ea5e9" }}>
                    <FaCheckCircle />
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="border-0 h-100" style={{ boxShadow: "0 12px 30px rgba(15, 23, 42, 0.08)" }}>
              <Card.Body className="d-flex flex-column justify-content-between">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted text-uppercase fw-semibold">Error Rate</small>
                    <h3 className="mt-2 mb-0">{overviewData.errorRate}%</h3>
                    <small className="text-danger">Lower is better</small>
                  </div>
                  <span className="rounded-3 d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, background: "rgba(248, 113, 113, 0.08)", color: "#ef4444" }}>
                    <FaExclamationTriangle />
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters row */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col md={3} sm={6}>
                <Form.Group>
                  <Form.Label className="small text-muted">Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={pendingRange.startDate}
                    onChange={(e) => handlePendingDateChange('startDate', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group>
                  <Form.Label className="small text-muted">End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={pendingRange.endDate}
                    onChange={(e) => handlePendingDateChange('endDate', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group>
                  <Form.Label className="small text-muted">Quick Filter
                    <span className="ms-1 badge bg-light text-secondary rounded-pill">Sessions range</span>
                  </Form.Label>
                  <Form.Select
                    value={pendingQuickFilter}
                    onChange={(e) => handlePendingQuickFilter(e.target.value)}
                  >
                    {activeTab !== 'popular' && (<option value="last7days">Last 7 Days</option>)}
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last30days">Last 30 Days</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3} sm={6} className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  className="w-100 rounded-pill"
                  onClick={applyFilters}
                  disabled={
                    loading ||
                    (pendingRange.startDate && pendingRange.endDate && pendingRange.startDate > pendingRange.endDate)
                  }
                >
                  Apply Filters
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {errorMsg && (
          <Alert variant="warning" className="mb-3">
            {errorMsg}
          </Alert>
        )}

        {/* ── Sticky scroll-spy tab bar ───────────────────────────────── */}
        <div
          ref={tabBarRef}
          style={{
            position: 'sticky',
            top: '116px',          /* sits just below the admin top-navbar */
            zIndex: 190,           /* below admin navbar (bootstrap: 1030) but above page cards */
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(15,23,42,0.07)',
            padding: '5px 5px',
            marginBottom: '16px',
            display: 'flex',
            gap: '4px',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'none',
          }}
        >
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => scrollToSection(tab.key)}
              style={{
                border: 'none',
                outline: 'none',
                borderRadius: '20px',
                padding: '8px 22px',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.22s cubic-bezier(.4,0,.2,1)',
                background: activeTab === tab.key
                  ? 'linear-gradient(135deg, #2563eb, #4f46e5)'
                  : 'transparent',
                color: activeTab === tab.key ? '#fff' : '#6b7280',
                boxShadow: activeTab === tab.key
                  ? '0 4px 14px rgba(37,99,235,0.30)'
                  : 'none',
                letterSpacing: '0.01em',
                flexShrink: 0,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Stacked section content ─────────────────────────────────── */}

        {/* OVERVIEW */}
        <div ref={sectionRefs.overview} id="analytics-section-overview"
          style={{ scrollMarginTop: '116px', paddingTop: '16px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Overview</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderOverviewTab()}
            </Card.Body>
          </Card>
        </div>

        {/* PERFORMANCE */}
        <div ref={sectionRefs.performance} id="analytics-section-performance"
          style={{ scrollMarginTop: '116px', paddingTop: '20px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Performance</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderPerformanceTab()}
            </Card.Body>
          </Card>
        </div>

        {/* ENGAGEMENT */}
        <div ref={sectionRefs.engagement} id="analytics-section-engagement"
          style={{ scrollMarginTop: '116px', paddingTop: '20px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Engagement</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderEngagementTab()}
            </Card.Body>
          </Card>
        </div>

        {/* DEVICES */}
        <div ref={sectionRefs.devices} id="analytics-section-devices"
          style={{ scrollMarginTop: '116px', paddingTop: '20px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Devices</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderDevicesTab()}
            </Card.Body>
          </Card>
        </div>

        {/* SESSIONS */}
        <div ref={sectionRefs.sessions} id="analytics-section-sessions"
          style={{ scrollMarginTop: '116px', paddingTop: '20px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Sessions</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderSessionsTab()}
            </Card.Body>
          </Card>
        </div>

        {/* POPULAR */}
        <div ref={sectionRefs.popular} id="analytics-section-popular"
          style={{ scrollMarginTop: '116px', paddingTop: '20px', paddingBottom: '24px' }}
        >
          <Card className="border-0 shadow-sm mb-0">
            <Card.Header className="bg-white border-0 py-3">
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <span style={{ width: 16, height: 16, borderLeft: '4px solid #f59e0b', borderBottom: '4px solid #f59e0b', display: 'inline-block', flexShrink: 0 }} />
                <h5 className="mb-0 fw-bold text-dark" style={{ letterSpacing: '0.01em' }}>Popular</h5>
              </div>
            </Card.Header>
            <Card.Body className="pt-0">
              {renderPopularTab()}
            </Card.Body>
          </Card>
        </div>

        {renderVideoDetailsModal()}

        {/* Real-time footer strip */}
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body className="py-3">
            <Row>
              <Col md={4} sm={12} className="mb-2 mb-md-0">
                <div className="realtime-metric">
                  <span className="metric-label">Active Sessions</span> :
                  <span className="metric-value live-value"> {realTimeMetrics.activeSessions}</span>
                </div>
              </Col>
              <Col md={4} sm={12} className="mb-2 mb-md-0">
                <div className="realtime-metric">
                  <span className="metric-label">Current Viewers</span> :
                  <span className="metric-value live-value"> {realTimeMetrics.currentViewers}</span>
                </div>
              </Col>
              <Col md={4} sm={12}>
                <div className="realtime-metric">
                  <span className="metric-label">Peak Concurrent</span> :
                  <span className="metric-value"> {realTimeMetrics.peakConcurrentUsers}</span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default PlayerAnalytics;

